import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { stringify } from "query-string";
import { matchesBoolean, matchesString } from "LIB/filters";
import {
  Pagination,
  SearchFilter,
  TitleWithFilters,
  FilterSelect
} from "MODULES/shared/components/Pagination";
import { Authorized, Button, Search } from "MODULES/shared/components";
import {
  ACTIVE,
  COMPANY_TYPE,
  COMPANY_TYPES,
  CREDIT_HOLD,
  MANAGER_CONSENT_NEEDED,
  NO_BIZ,
  OBSOLETE,
  OUT_OF_BIZ,
  REPAIR_STATION_STATUS,
  STATUS
} from "MODULES/companies/constants";
import { properCase } from "LIB/formatters/text";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import {
  ACCOUNTING,
  CAN_ADD_COMPANIES,
  CAN_UPDATE_COMPANIES,
  QUALITY_CONTROL,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  ADMIN,
  CAN_EXPORT_COMPANIES
} from "MODULES/users/constants";
import { hasPermission, hasRole } from "MODULES/users/helpers";
import { API_BASE, CLEAR_FILTERS_TEXT } from "APP_ROOT/constants";
import { renderIf } from "MODULES/shared/decorators";
import { toastr } from "react-redux-toastr";
import api from "APP_ROOT/api";
import { SUCCESS } from "LIB/submission";
import Table from "./Table";
import NewCompany from "./NewCompany";
import { companiesPagination } from "../../pagination";

const defaultFilters = {
  companies_with_fleet: undefined,
  demerits_past_24_months: undefined,
  repair_station_status: undefined,
  status: undefined,
  do_not_exchange: undefined,
  do_not_quote: undefined,
  blanket_exchange_agreement: undefined,
  search_billing_address: undefined
};

const companyTypeFilters = Object.keys(COMPANY_TYPES).map(k => ({
  label: COMPANY_TYPES[k].label,
  filters: { company_type: k }
}));

const filters = [
  {
    label: "Active Demerits",
    filters: { demerits_past_24_months: true }
  },
  {
    label: "Blanket Exch Agreement",
    filters: { blanket_exchange_agreement: true }
  },
  {
    label: properCase(NO_BIZ),
    filters: { status: NO_BIZ }
  },
  {
    label: properCase(OUT_OF_BIZ),
    filters: { status: OUT_OF_BIZ }
  },
  {
    label: properCase(ACTIVE),
    filters: { status: ACTIVE }
  },
  {
    label: properCase(OBSOLETE),
    filters: { status: OBSOLETE }
  },
  {
    label: "Do Not Exchange",
    filters: { do_not_exchange: true }
  },
  {
    label: "Do Not Quote",
    filters: { do_not_quote: true }
  },
  {
    label: "Credit Hold",
    filters: { status: CREDIT_HOLD }
  },
  {
    label: "Repair Stations w/ Manager Consent Needed",
    filters: {
      repair_station_status: MANAGER_CONSENT_NEEDED
    }
  },
  {
    label: "Show Companies With Fleet",
    filters: { companies_with_fleet: true },
    hr: true
  },
  {
    label: CLEAR_FILTERS_TEXT,
    filters: defaultFilters
  }
];

const filterTags = [
  {
    label: properCase(CREDIT_HOLD),
    key: STATUS,
    matchesTarget: matchesString(CREDIT_HOLD)
  },
  {
    label: "Active Demerits",
    key: "demerits_past_24_months",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Do Not Exchange",
    key: "do_not_exchange",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Do Not Quote",
    key: "do_not_quote",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Blanket Exch Agreement",
    key: "blanket_exchange_agreement",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: properCase(NO_BIZ),
    key: STATUS,
    matchesTarget: matchesString(NO_BIZ)
  },
  {
    label: properCase(OUT_OF_BIZ),
    key: STATUS,
    matchesTarget: matchesString(OUT_OF_BIZ)
  },
  {
    label: properCase(ACTIVE),
    key: STATUS,
    matchesTarget: matchesString(ACTIVE)
  },
  {
    label: properCase(OBSOLETE),
    key: STATUS,
    matchesTarget: matchesString(OBSOLETE)
  },
  {
    label: properCase(MANAGER_CONSENT_NEEDED),
    key: REPAIR_STATION_STATUS,
    matchesTarget: matchesString(MANAGER_CONSENT_NEEDED)
  },
  {
    label: "Companies With Fleet",
    key: "companies_with_fleet"
  },
  ...Object.keys(COMPANY_TYPES).map(k => ({
    label: COMPANY_TYPES[k].label,
    key: "company_type",
    matchesTarget: matchesString(k)
  }))
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const totalCount = searchProps.paginator.get("totalCount");
  const currentUser = useCurrentUser();
  if (
    !hasPermission(currentUser, CAN_EXPORT_COMPANIES) ||
    (hasPermission(currentUser, CAN_EXPORT_COMPANIES) &&
      totalCount > 475 &&
      currentUser.role !== ADMIN)
  ) {
    return null;
  }

  params.total_count = totalCount;
  params.sort = "name";
  params.search_billing_address = true;
  return (
    <Button
      onClick={() => {
        if (totalCount > 475) {
          toastr.info("Processing Report.");
          api.companies.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/companies/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Companies Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

const meetsExportCriteria = renderIf(({ searchProps }) => {
  const user = useCurrentUser();
  return searchProps.paginator.get("totalCount") <= 450 || hasRole(user, ADMIN);
});

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function CompaniesSearch({ pushHistory }) {
  return (
    <Search search={companiesPagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a company name, code, contact name, contact email, city, state/province, country, region or fleet type..."
                submitOnSingleResult={r => pushHistory(`/companies/${r.uuid}`)}
              />
              <DecoratedExportButton searchProps={searchProps} />
              <FilterSelect
                title={properCase(COMPANY_TYPE)}
                {...searchProps}
                filters={companyTypeFilters}
                right
              />
              <FilterSelect {...searchProps} filters={filters} right />
              <Authorized
                subroles={[WAREHOUSE_LOGISTICS_DIRECTOR]}
                roles={[QUALITY_CONTROL, ACCOUNTING]}
                permissions={[CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES]}
              >
                <NewCompany />
              </Authorized>
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Companies"
            filters={filterTags}
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

CompaniesSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, { pushHistory: push })(CompaniesSearch);



// WEBPACK FOOTER //
// ./app/modules/companies/components/Search/Search.jsx